import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, UntypedFormGroup } from '@angular/forms';
import { CustomFormConstructorService } from '../form-constructor/custom-form-constructor.service';
import { Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FieldGroupsService } from '../../../core/api/field-groups/field-groups.service';
import { get, isEmpty, keyBy, uniq } from 'lodash-es';
import { SidenavScreenSizeService } from '../../../fury-core/sidenav/sidenav-screen-size.service';

@Component({
  standalone: false,
  selector: 'gd-custom-data-panel',
  templateUrl: './custom-data-panel.component.html',
})
export class CustomDataPanelComponent implements OnInit, OnChanges, OnDestroy {
  // configuration for construction of the form model
  @Input() panelConfiguration;
  // data to be used for initial values of the form
  @Input() panelData = {};
  // the parent form group in which panelFormGroup will be embedded
  @Input() customDataFormGroup: UntypedFormGroup;
  @Input() hasActionPermission = true;
  @Input() isEditing;
  @Input() usage;
  @Input() activeLocale;

  // used for full single column layout, currently only on live report
  @Input() singleColumn = false;

  visiblePanelConfiguration = [];
  panelFormGroup: UntypedFormGroup = null;
  componentSub: Subscription = new Subscription();
  panelInitialized = false;
  prepared = false;
  isScreenSmall = false;
  breakpointSub;
  displayedConfiguration;

  constructor(
    private fc: CustomFormConstructorService,
    private sidenavScreenSizeService: SidenavScreenSizeService,
    private customFieldGroupService: FieldGroupsService,
  ) { }

  ngOnInit() {
    if (this.panelConfiguration) {
      this.prepared = true;
      this.initializePanel();
    }

    // trigger two column layout if desktop, otherwise show single column
    this.componentSub.add(
      this.sidenavScreenSizeService.getSizeState().subscribe((screenSize) => {
        this.isScreenSmall = screenSize.isMobile;
      })
    );
  }

  initializePanel() {
    // TODO remove the prepared and panelInitialized helpers, there were not used on collections panel
    // TODO ensure that panel gets initialized only once as on collections
    // avoid initializing the panel multiple times as it causes issues
    if (this.panelInitialized) {
      return;
    }
    this.panelInitialized = true;

    const options = {
      editMode: this.isEditing,
      contentLocaleId: this.activeLocale?.id || null,
    };
    this.panelFormGroup = this.fc.createFormGroup(this.panelConfiguration, this.panelData, options);
    this.customDataFormGroup.addControl('metaData', this.panelFormGroup);

    // this hides the fields that have the new 'hidden' attribute set as true
    this.visiblePanelConfiguration = this.panelConfiguration.filter(field => !field.hidden);

    this.initializeCfgFields();

  }

  ngOnChanges(): void {
    // init once the form group and form config are available
    // panel data does not need to be immediately available
    const readyForInit = this.customDataFormGroup && this.panelConfiguration && this.prepared;
    if (readyForInit) {
      this.initializePanel();
    }
  }

  ngOnDestroy() {
    this.componentSub.unsubscribe();
  }

  // TODO refactor this and move most of the code into `CustomFormConstructorService` as possible
  // fetch, process, and initialize CFGs
  initializeCfgFields() {
    const cfgFields = this.panelConfiguration.filter((f) => f.fieldType === 'group');
    const cfgIds = uniq(cfgFields.map((field) => field.dataSource.value));

    // no CFGs to initialize
    if(isEmpty(cfgIds)) {
      return;
    }

    // add validators making the form invalid during the loading period
    // TODO add the validators immediately on creating the CFG form groups, then disarm later
    this.panelFormGroup.removeValidators(cfgLoadingValidator);
    this.panelFormGroup.addValidators(cfgLoadingValidator);
    this.panelFormGroup.updateValueAndValidity();

    // TODO see if this van go into effects
    this.customFieldGroupService
      .getReferencedCustomFieldGroupsByIds(cfgIds)
      .pipe(catchError(error => {
        console.error(error);
        return [];
      }))
      .subscribe((fieldGroupsData) => {
        // map fetched CFG into a dictionary by id for ease of use
        const fieldGroups = keyBy(fieldGroupsData, 'id');

        // do form init and additionalData source so that component doesn't have to do it
        this.panelConfiguration = this.panelConfiguration.map((field) => {
          // non CFG fields stay as is
          if (field.fieldType !== 'group') {
            return field;
          }

          // get the CFG from the API response
          const cfgId = field.dataSource.value;
          const cfg = fieldGroups[cfgId] || null;

          // TODO handle if the CFG is gone or not fetched!
          if(!cfg) {
            const syntheticError = { error: 'Cannot fetch CFG', id: cfgId };
            return { ...field, additionalDataSource: syntheticError };
          }

          const formControl: AbstractControl = this.panelFormGroup.get(field.key);
          const initialContextValues = this.panelData;
          const initialValues = get(this.panelData, field.key, null);

          const isCfgList = field.inputType === 'multiple';
          // handle form fragment lists
          if (isCfgList) {
            this.fc.initializeCfgFormArray(
                cfg,
                formControl as FormArray,
                this.isEditing,
                this.activeLocale,
                initialValues
              );
          }

          // handle regular single form fragments (CFGs)
          if(!isCfgList) {
            this.fc.initializeCfgFormGroup(
              cfg,
              formControl as FormGroup,
              this.isEditing,
              this.activeLocale,
              initialValues,
              initialContextValues
            );
          }

          // return the updated cfg field config
          return {
            ...field,
            // we use this to pass along the loaded CFG config instead of it being fetched in component
            additionalDataSource: cfg,
          };
        });

        // remove loading validators
        this.panelFormGroup.removeValidators(cfgLoadingValidator);
        this.panelFormGroup.updateValueAndValidity();

        //update visible panel configurations to trigger re-rendering of loaded CFGs
        this.visiblePanelConfiguration = this.panelConfiguration.filter(field => !field.hidden);
      });

  }
}

/**
 * This validator always returns an error, indicating that CFG config is loading.
 * Once the config is loaded we remove the validator, thus removing the error too.
 */
function cfgLoadingValidator() {
  return { cfgConfigurationLoading: true };
}
